import { useEffect, useState, type ReactNode } from 'react';
import { DollarSign, Percent, Gift, type LucideIcon } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  useCreateCoupon,
  type CreateCouponInput,
  type DiscountType,
  type CouponScope,
  type UsageType,
} from '@/hooks/customer-engagement/use-coupons';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Standard form tokens — align with the Create Customer redesign. Text/number
// inputs use the shared <Input>; these tokens cover the controls that aren't
// <Input> (native <select>).
const inputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;

// The validity pickers edit wall-clock time in the operator's own browser zone —
// the same semantics as the native datetime-local input they replace. Pin the
// DatePicker to that zone and store the same `YYYY-MM-DDTHH:mm` string the
// backend already receives, so nothing downstream changes.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
function dateToLocalDatetime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Discount-type picker cards — icon + one-line description per type, mirroring
// the Customer Type cards in the Create Customer dialog. Order/values are
// unchanged from the old <select>; this drives presentation only.
const DISCOUNT_TYPE_META: {
  value: DiscountType;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  { value: 'amount_off', label: '$ off', description: 'Flat amount off', icon: DollarSign },
  { value: 'percent_off', label: '% off', description: 'Percentage off', icon: Percent },
  { value: 'free_product', label: 'Free product', description: 'Give an item free', icon: Gift },
];

const EMPTY: CreateCouponInput = {
  code: '',
  name: '',
  discountType: 'amount_off',
  discountValue: 0,
  scope: 'order',
  minSpend: 0,
  usageType: 'multi_use',
  audience: 'all',
  locationScope: 'all',
  showOnRegister: true,
};

export function CouponCreateDialog({ open, onClose }: Props) {
  const [form, setForm] = useState<CreateCouponInput>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const create = useCreateCoupon();

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setFieldErrors({});
  }, [open]);

  const handleClose = () => {
    setForm(EMPTY);
    setFieldErrors({});
    onClose();
  };

  const set = <K extends keyof CreateCouponInput>(key: K, value: CreateCouponInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((e) => {
        const { [key as string]: _, ...rest } = e;
        return rest;
      });
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!form.code.trim()) errors.code = 'Code is required';
    if (!form.name.trim()) errors.name = 'Name is required';
    if (form.discountType !== 'free_product') {
      const v = form.discountValue;
      if (form.discountType === 'percent_off' && (v < 0 || v > 100)) {
        errors.discountValue = 'Percent must be between 0 and 100';
      } else if (form.discountType === 'amount_off' && v < 0) {
        errors.discountValue = 'Amount cannot be negative';
      }
    }
    if (form.validFrom && form.validTo && new Date(form.validFrom) >= new Date(form.validTo)) {
      errors.validTo = 'Valid to must be after Valid from';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await create.mutateAsync({
        ...form,
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        validFrom: form.validFrom || undefined,
        validTo: form.validTo || undefined,
      });
      toast.success(`Coupon ${form.code.trim().toUpperCase()} created`);
      handleClose();
    } catch (e: any) {
      toast.error(e?.error?.message ?? 'Failed to create coupon');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Coupon"
      widthClass="max-w-[720px]"
      footer={
        <div className="flex items-center gap-3">
          <span className="mr-auto hidden text-xs text-muted-foreground sm:block">Code and name are required.</span>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="coupon-create-form" disabled={create.isPending}>
            {create.isPending ? 'Creating...' : 'Create Coupon'}
          </Button>
        </div>
      }
    >
      <form id="coupon-create-form" onSubmit={handleSubmit} noValidate className="space-y-7">
        {/* ── 1 · basic details (code + name + description) ─────────── */}
        <FormSection num={1} title="Basic Details">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="coupon-code">
                Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="coupon-code"
                className="mt-1 font-mono uppercase"
                type="text"
                value={form.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="SAVE5"
                maxLength={50}
                error={!!fieldErrors.code}
                aria-invalid={!!fieldErrors.code}
              />
              {fieldErrors.code && <p className="mt-1 text-xs text-red-500">{fieldErrors.code}</p>}
            </div>
            <div>
              <Label htmlFor="coupon-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="coupon-name"
                className="mt-1"
                type="text"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="$5 off $25"
                maxLength={120}
                error={!!fieldErrors.name}
                aria-invalid={!!fieldErrors.name}
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
            </div>
          </div>

          <div>
            <Label htmlFor="coupon-description">Description (shown on receipt)</Label>
            <Input
              id="coupon-description"
              className="mt-1"
              type="text"
              value={form.description ?? ''}
              onChange={(e) => set('description', e.target.value)}
              maxLength={500}
            />
          </div>
        </FormSection>

        {/* ── 2 · discount ─────────────────────────────────────────── */}
        <FormSection num={2} title="Discount">
          <div>
            <Label>Discount type</Label>
            <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {DISCOUNT_TYPE_META.map((t) => {
                const Icon = t.icon;
                const selected = form.discountType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => set('discountType', t.value)}
                    aria-pressed={selected}
                    className={cn(
                      'flex items-start gap-3 rounded-xl border p-3 text-left transition',
                      selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">{t.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{t.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {form.discountType !== 'free_product' ? (
            <div className="sm:max-w-xs">
              <Label htmlFor="coupon-discount-value">
                {form.discountType === 'amount_off' ? 'Amount ($)' : 'Percent (0-100)'}
                <span className="text-red-500"> *</span>
              </Label>
              <Input
                id="coupon-discount-value"
                className="mt-1"
                type="number"
                selectOnFocus
                value={form.discountValue}
                onChange={(e) => set('discountValue', Number(e.target.value))}
                step={form.discountType === 'amount_off' ? '0.01' : '1'}
                min="0"
                max={form.discountType === 'percent_off' ? 100 : undefined}
                error={!!fieldErrors.discountValue}
                aria-invalid={!!fieldErrors.discountValue}
              />
              {fieldErrors.discountValue && <p className="mt-1 text-xs text-red-500">{fieldErrors.discountValue}</p>}
            </div>
          ) : (
            <div className="sm:max-w-xs">
              <Label htmlFor="coupon-free-product">Free product ID</Label>
              <Input
                id="coupon-free-product"
                className="mt-1"
                type="text"
                value={form.freeProductId ?? ''}
                onChange={(e) => set('freeProductId', e.target.value || undefined)}
                placeholder="product UUID"
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="coupon-scope">Apply to</Label>
              <select
                id="coupon-scope"
                value={form.scope}
                onChange={(e) => set('scope', e.target.value as CouponScope)}
                className={`mt-1 ${inputNormal}`}
              >
                <option value="order">Entire order</option>
                <option value="products">Specific products</option>
                <option value="exclude_categories">All except certain categories</option>
              </select>
            </div>
            <div>
              <Label htmlFor="coupon-min-spend">Minimum spend ($)</Label>
              <Input
                id="coupon-min-spend"
                className="mt-1"
                type="number"
                selectOnFocus
                step="0.01"
                min="0"
                value={form.minSpend ?? 0}
                onChange={(e) => set('minSpend', Number(e.target.value))}
              />
            </div>
          </div>
        </FormSection>

        {/* ── 3 · validity window ──────────────────────────────────── */}
        <FormSection num={3} title="Validity window">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="coupon-valid-from">Valid from</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={form.validFrom ? new Date(form.validFrom) : null}
                onChange={(v) => set('validFrom', v instanceof Date ? dateToLocalDatetime(v) : undefined)}
                aria-label="Valid from"
              />
            </div>
            <div>
              <Label htmlFor="coupon-valid-to">Valid to</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={form.validTo ? new Date(form.validTo) : null}
                onChange={(v) => set('validTo', v instanceof Date ? dateToLocalDatetime(v) : undefined)}
                error={!!fieldErrors.validTo}
                aria-label="Valid to"
              />
              {fieldErrors.validTo && <p className="mt-1 text-xs text-red-500">{fieldErrors.validTo}</p>}
            </div>
          </div>
        </FormSection>

        {/* ── 4 · usage limits ─────────────────────────────────────── */}
        <FormSection num={4} title="Usage limits">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="coupon-usage-type">Usage type</Label>
              <select
                id="coupon-usage-type"
                value={form.usageType}
                onChange={(e) => set('usageType', e.target.value as UsageType)}
                className={`mt-1 ${inputNormal}`}
              >
                <option value="multi_use">Multi-use</option>
                <option value="single_use">Single-use</option>
              </select>
            </div>
            <div>
              <Label htmlFor="coupon-max-uses">Max uses per customer</Label>
              <Input
                id="coupon-max-uses"
                className="mt-1"
                type="number"
                selectOnFocus
                min="1"
                value={form.maxUsesPerCustomer ?? ''}
                onChange={(e) => set('maxUsesPerCustomer', e.target.value === '' ? undefined : Number(e.target.value))}
                placeholder="unlimited"
              />
            </div>
            <div>
              <Label htmlFor="coupon-first-n">First N customers (cap)</Label>
              <Input
                id="coupon-first-n"
                className="mt-1"
                type="number"
                selectOnFocus
                min="1"
                value={form.firstNCap ?? ''}
                onChange={(e) => set('firstNCap', e.target.value === '' ? undefined : Number(e.target.value))}
                placeholder="unlimited"
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.showOnRegister ?? true}
              onChange={(e) => set('showOnRegister', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Show this coupon on the register so cashiers can pick from a list
          </label>
        </FormSection>
      </form>
    </Modal>
  );
}

// A numbered, titled group of fields — the same visual "spine" used by the
// Create Customer and Promotion dialogs, so all three read as one design
// system. Purely presentational — no state, no logic.
function FormSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold leading-none text-primary">
          {num}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
